import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { routerActions } from 'connected-react-router';
import InlineDropdown from 'platform/common/components/InlineDropdown/InlineDropdown';
import UpdatedOn from 'platform/common/components/UpdatedOn/UpdatedOn';
import ListToolbar from 'platform/common/components/ListToolbar/ListToolbar';
import FormattedTable from 'platform/common/components/FormattedTable/FormattedTable';
import PageHeader from 'platform/common/components/PageHeader/PageHeader';
import { DATA_TYPES } from 'platform/common/dataType';
import { TableCell } from 'platform/common/common.type';
import { AdvertiserFormCreate, AdvertiserFormEdit } from './AdvertiserForm';
import * as advertiserService from './advertiser.service';
import { Advertiser } from './advertiser.type';

type State = {
    loading: boolean;
    data: Advertiser[];
    searchQuery: string;
};

const AdvertiserList = () => {
    const dispatch = useDispatch();

    const [state, setState] = useState<State>({
        loading: true,
        data: [],
        searchQuery: '',
    });

    const fetchAdvertisers = () => {
        setState(currentState => ({ ...currentState, loading: true }));
        advertiserService
            .fetchAdvertisers()
            .then(response => {
                setState(currentState => ({ ...currentState, data: response }));
            })
            .finally(() => {
                setState(currentState => ({ ...currentState, loading: false }));
            });
    };

    useEffect(() => {
        fetchAdvertisers();
    }, []);

    const columnDefinitions = [
        {
            Header: 'ID',
            accessor: 'id',
            type: DATA_TYPES.ID,
            width: 80,
        },
        {
            Header: 'Name',
            minWidth: 200,
            type: DATA_TYPES.TEXT,
            Cell: (row: TableCell<Advertiser>) => <span>{row.original.name}</span>,
        },
        {
            Header: 'URL',
            minWidth: 200,
            type: DATA_TYPES.TEXT,
            Cell: (row: TableCell<Advertiser>) => <span>{row.original.url}</span>,
        },
        {
            Header: 'External ID',
            minWidth: 200,
            type: DATA_TYPES.TEXT,
            Cell: (row: TableCell<Advertiser>) => <span>{row.original.externalId}</span>,
        },
        {
            Header: 'Edited',
            accessor: 'lastModifiedOn',
            width: 160,
            Cell: (row: TableCell<Advertiser>) => (
                <UpdatedOn date={row.original.lastModifiedOn} updatedBy={row.original.lastModifiedBy} />
            ),
        },
        {
            className: () => 'edit-dropdown pull-right cell-align-right',
            width: 50,
            sortable: false,
            Cell: (row: TableCell<Advertiser>) => (
                <InlineDropdown
                    items={[
                        {
                            label: 'Edit',
                            action: () =>
                                dispatch(routerActions.push(`/admin-panel/advertisers/${row.original.id}`)),
                        },
                    ]}
                />
            ),
        },
    ];

    const filterList = (list: Advertiser[], searchQuery: string) => {
        if (!searchQuery) return list;
        const query = searchQuery.toLowerCase();
        return list.filter(
            r =>
                String(r.id)
                    .toLowerCase()
                    .includes(query) || r.name.toLowerCase().includes(query)
        );
    };

    return (
        <div>
            <div className="my-4">
                <PageHeader title="Advertisers" />
            </div>
            <div className="mb-4">
                <ListToolbar
                    value={state.searchQuery}
                    onCreate={() => dispatch(routerActions.push('/admin-panel/advertisers/new'))}
                    onSearch={searchQuery => setState(currentState => ({ ...currentState, searchQuery }))}
                />
            </div>
            <FormattedTable
                columns={columnDefinitions}
                data={filterList(state.data, state.searchQuery)}
                loading={state.loading}
            />
            <Switch>
                <Route
                    path="/admin-panel/advertisers/new"
                    render={props => (
                        <AdvertiserFormCreate
                            {...props}
                            redirectTo="/admin-panel/advertisers"
                            canEdit
                            afterSubmit={(advertiser: Advertiser) =>
                                dispatch(routerActions.push(`/admin-panel/advertisers/${advertiser.id}`))
                            }
                        />
                    )}
                />
                <Route
                    path="/admin-panel/advertisers/:id"
                    render={props => (
                        <AdvertiserFormEdit
                            {...props}
                            redirectTo="/admin-panel/advertisers"
                            canEdit
                            onClose={() => fetchAdvertisers()}
                        />
                    )}
                />
            </Switch>
        </div>
    );
};

export default AdvertiserList;
